import {
    KPI_VALUE_CLASSIC,
    KPI_VALUE_MEDIA_VIEWER_CLASSIC,
    KPI_VALUE_MEDIA_VIEWER_PERCENT,
    KPI_VALUE_PERCENT
} from '@/hooks/format/useFormatKpiValue';

// Types
import type { UserNumberFormatType } from '@/types/userType';

// Libs
import { CURRENCY_SYMBOLS } from '@/lib/billing/billing';

export const percentFormat: Intl.NumberFormatOptions = {
    style: 'percent',
    maximumFractionDigits: 2
};

export const percentFormatCompact: Intl.NumberFormatOptions = {
    ...percentFormat,
    notation: 'compact'
};

export const classicFormat: Intl.NumberFormatOptions = {
    notation: 'compact',
    maximumFractionDigits: 1,
    compactDisplay: 'short'
};

export const classicFormatMaxTwoDigits: Intl.NumberFormatOptions = {
    ...classicFormat,
    maximumFractionDigits: 2
};

export const classicCurrencyFormat: Intl.NumberFormatOptions = {
    minimumFractionDigits: 2
};

type getNumberFormatType =
    | typeof KPI_VALUE_CLASSIC
    | typeof KPI_VALUE_PERCENT
    | typeof KPI_VALUE_MEDIA_VIEWER_PERCENT
    | typeof KPI_VALUE_MEDIA_VIEWER_CLASSIC;

export const getNumberFormat = (format: getNumberFormatType) => {
    switch (format) {
        case KPI_VALUE_CLASSIC:
            return classicFormat;
        case KPI_VALUE_PERCENT:
        case KPI_VALUE_MEDIA_VIEWER_PERCENT:
            return percentFormat;
        case KPI_VALUE_MEDIA_VIEWER_CLASSIC:
            return {};
        default:
            return classicFormat;
    }
};

const useFormatNumber = (format: UserNumberFormatType) => {
    const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => {
        switch (format) {
            case 'space':
                return new Intl.NumberFormat('fr', { ...options }).format(value);
            case 'comma':
                return new Intl.NumberFormat('en', { ...options }).format(value);
            case 'dot':
                return new Intl.NumberFormat('es', { ...options }).format(value).includes('mil')
                    ? new Intl.NumberFormat('es', { ...options }).format(value).replace('mil', 'k')
                    : new Intl.NumberFormat('es', { ...options }).format(value);

            default:
                return new Intl.NumberFormat('en', { ...options }).format(value);
        }
    };

    const formatCurrency = (value: number, options?: Intl.NumberFormatOptions) => {
        switch (format) {
            case 'space':
                return new Intl.NumberFormat('fr', { ...options }).format(value);
            case 'comma':
                return new Intl.NumberFormat('en', { ...options }).format(value);
            case 'dot':
                return new Intl.NumberFormat('de', { ...options }).format(value);

            default:
                return new Intl.NumberFormat('en', { ...options }).format(value);
        }
    };

    const formatPriceCurrencySymbol = (
        value: number,
        currency: keyof typeof CURRENCY_SYMBOLS,
        useFormatCurrency = true
    ) => {
        const valueReturn = useFormatCurrency
            ? formatCurrency(value, classicCurrencyFormat)
            : value;

        return currency === 'EUR' || currency === 'BRL' || currency === 'INR'
            ? `${valueReturn} ${CURRENCY_SYMBOLS[currency]}`
            : `${CURRENCY_SYMBOLS[currency]} ${valueReturn}`;
    };

    const formatPriceSymbolNoSpace = (value: number, currency: keyof typeof CURRENCY_SYMBOLS) => {
        //Add check if value has decimal
        const price =
            value % 1 !== 0 ? formatCurrency(value, classicCurrencyFormat) : formatCurrency(value);

        return currency === 'EUR' || currency === 'BRL' || currency === 'INR'
            ? `${price}${CURRENCY_SYMBOLS[currency]}`
            : `${CURRENCY_SYMBOLS[currency]}${price}`;
    };

    return { formatNumber, formatCurrency, formatPriceCurrencySymbol, formatPriceSymbolNoSpace };
};

export default useFormatNumber;
